import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { useFetch } from "@/hooks/useFetch";
import { formatDateUTC7 } from "@/lib/date.utils";
import { ListResponse } from "@/types/app.type";
import {
  Bell,
  Clock,
  MessageCircle,
  PlayCircle,
  Settings,
  StopCircle,
  XCircle,
  CheckCheck,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  getNotificationList,
  getNotificationUnreadCount,
  readNotification,
  readAllNotification,
} from "../api/api-notification";
import {
  Notification,
  NotificationListParams,
  NotificationType,
} from "../types/notification.type";

const DEFAULT_NOTIFICATION_LIST_PARAMS: NotificationListParams = {
  page: 1,
  pageSize: 100,
  sortBy: "createdAt",
  sortOrder: "desc",
};

export function NotificationList() {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState<number | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [open, setOpen] = useState(false);
  const currentPageRef = useRef(1);

  const { handleFetch: handleFetchNotifications, isLoading } = useFetch({
    fetchFunc: (params: NotificationListParams) => getNotificationList(params),
    options: {
      onSuccess: (data) => {
        const res = data as unknown as ListResponse<Notification>;
        const page = res.paging?.page ?? 1;
        currentPageRef.current = page;

        setNotifications((prev) => (page === 1 ? res.data : [...prev, ...res.data]));

        const nextPage = res.paging?.nextPage;
        if (nextPage !== null && nextPage !== undefined) {
          setHasMore(true);
        } else if (res.paging) {
          setHasMore(false);
        } else {
          // Fallback nếu backend không trả paging
          setHasMore((res.data?.length ?? 0) >= (DEFAULT_NOTIFICATION_LIST_PARAMS.pageSize ?? 10));
        }

        setIsLoadingMore(false);
      },
      onFail: () => {
        setIsLoadingMore(false);
      },
    },
  });

  const { handleFetch: handleFetchUnreadCount } = useFetch({
    fetchFunc: () => getNotificationUnreadCount(),
    options: {
      onSuccess: (data) => {
        setUnreadCount(data?.data?.unreadCount ?? 0);
      },
    },
  });

  const { handleFetch: handleReadNotification } = useFetch({
    fetchFunc: (notificationUserId: string) => readNotification(notificationUserId),
    options: {
      onSuccess: () => {
        currentPageRef.current = 1;
        setHasMore(true);
        setIsLoadingMore(false);
        handleFetchNotifications(DEFAULT_NOTIFICATION_LIST_PARAMS);
        handleFetchUnreadCount({});
      },
    },
  });

  const { handleFetch: handleReadAllNotifications } = useFetch({
    fetchFunc: () => readAllNotification(),
    options: {
      onSuccess: () => {
        currentPageRef.current = 1;
        setHasMore(true);
        setIsLoadingMore(false);
        handleFetchNotifications(DEFAULT_NOTIFICATION_LIST_PARAMS);
        handleFetchUnreadCount({});
      },
    },
  });

  useEffect(() => {
    currentPageRef.current = 1;
    setHasMore(true);
    setIsLoadingMore(false);
    handleFetchNotifications(DEFAULT_NOTIFICATION_LIST_PARAMS);
    handleFetchUnreadCount({});
  }, []);

  useEffect(() => {
    if (open) {
      currentPageRef.current = 1;
      setHasMore(true);
      setIsLoadingMore(false);
      handleFetchNotifications(DEFAULT_NOTIFICATION_LIST_PARAMS);
      handleFetchUnreadCount({});
    }
  }, [open]);

  const derivedUnreadCount = notifications.filter((n) => !n.isRead).length;
  const displayUnreadCount = unreadCount ?? derivedUnreadCount;

  const getIcon = useCallback((type: NotificationType) => {
    const iconClass = "w-5 h-5 flex-shrink-0";
    switch (type) {
      case NotificationType.MEETING_INVITE:
        return <Bell className={`${iconClass}`} />;
      case NotificationType.MEETING_REMINDER:
        return <Clock className={`${iconClass}`} />;
      case NotificationType.MEETING_STARTED:
        return <PlayCircle className={`${iconClass}`} />;
      case NotificationType.MEETING_ENDED:
        return <StopCircle className={`${iconClass}`} />;
      case NotificationType.MEETING_CANCELLED:
        return <XCircle className={`${iconClass}`} />;
      case NotificationType.SYSTEM:
        return <Settings className={`${iconClass}`} />;
      case NotificationType.NORMAL:
        return <MessageCircle className={`${iconClass}`} />;
      default:
        return <Bell className={`${iconClass}`} />;
    }
  }, []);

  const handleNotificationClick = useCallback(
    (notification: Notification) => {
      if (!notification.isRead) {
        handleReadNotification(notification.id);
      }
    },
    [handleReadNotification]
  );

  const handleMarkAllAsRead = useCallback(() => {
    if (displayUnreadCount > 0) {
      handleReadAllNotifications({});
    }
  }, [displayUnreadCount, handleReadAllNotifications]);

  const handleLoadMore = useCallback(() => {
    if (isLoading || isLoadingMore || !hasMore) return;

    setIsLoadingMore(true);
    const nextPage = currentPageRef.current + 1;
    currentPageRef.current = nextPage;

    handleFetchNotifications({
      ...DEFAULT_NOTIFICATION_LIST_PARAMS,
      page: nextPage,
    });
  }, [handleFetchNotifications, hasMore, isLoading, isLoadingMore]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors relative"
          aria-label="Thông báo"
        >
          <Bell className="w-6 h-6 text-gray-600 dark:text-gray-300" />
          {displayUnreadCount > 0 && (
            <Badge
              variant="destructive"
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
            >
              {displayUnreadCount > 9 ? "9+" : displayUnreadCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0" align="end">
        <div className="flex flex-col">
          <div className="flex items-center justify-between p-4 py-2 border-b">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Thông báo
            </h3>
            {displayUnreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleMarkAllAsRead}
                className="h-8 text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-950"
                aria-label="Đánh dấu tất cả đã đọc"
              >
                <CheckCheck className="w-4 h-4 mr-1" />
                Đánh dấu tất cả đã đọc
              </Button>
            )}
          </div>

          <ScrollArea className="h-[400px]">
            {notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 px-4">
                <Bell className="w-12 h-12 text-gray-300 dark:text-gray-600 mb-3" />
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Không có thông báo nào
                </p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100 dark:divide-gray-800">
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification)}
                    className={`
                      flex gap-3 p-4 cursor-pointer transition-colors
                      ${
                        notification.isRead
                          ? "bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800"
                          : "bg-blue-50 dark:bg-blue-950/20 hover:bg-blue-100 dark:hover:bg-blue-950/30"
                      }
                    `}
                    role="button"
                    tabIndex={0}
                    aria-label={`${notification.title}. ${notification.content}`}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleNotificationClick(notification);
                      }
                    }}
                  >
                    <div className="flex-shrink-0 mt-0.5">
                      {getIcon(notification.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <h4
                          className={`
                            text-sm font-medium leading-tight
                            ${
                              notification.isRead
                                ? "text-gray-700 dark:text-gray-300"
                                : "text-gray-900 dark:text-gray-100 font-semibold"
                            }
                          `}
                        >
                          {notification.title}
                        </h4>
                        {!notification.isRead && (
                          <div className="w-2 h-2 rounded-full bg-blue-600 flex-shrink-0 mt-1" />
                        )}
                      </div>
                      <p
                        className={`
                          text-xs leading-relaxed mb-2
                          ${
                            notification.isRead
                              ? "text-gray-500 dark:text-gray-400"
                              : "text-gray-700 dark:text-gray-300"
                          }
                        `}
                      >
                        {notification.content}
                      </p>
                      <p className="text-xs text-gray-400 dark:text-gray-500">
                        {formatDateUTC7(notification.createdAt, "relative", "vi-VN")}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>

          {hasMore && (
            <div className="p-3 border-t">
              <Button
                variant="outline"
                size="sm"
                className="w-full text-xs"
                onClick={handleLoadMore}
                disabled={isLoading || isLoadingMore}
                aria-label="Xem thêm thông báo"
              >
                {isLoadingMore ? "Đang tải..." : "Xem thêm"}
              </Button>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
